import { Box, Portal, Stack } from '@mui/material';
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import ChatBG from '../../assets/images/ChatBG';
import { getUserNotificationsCount } from '../../services/login-service';
import { RootState } from '../../store';
import { setUserNotificationsCount } from '../../store/reducers/userSlice';
import { NavigationConfig, stripTrailingSlashes } from '../../utils/navigation-constants';
import { DrawerVisibilityContext } from '../drawer/CustomDrawer';
import { colors } from '../theme/theme';

import { AuxCapAssistantDrawer } from './AuxCapAssistantDrawer';
import { Header } from './header';
import { Sidebar, SidebarRef } from './sidebar';

/** Same condition as header: verification banner is visible when primary phone is unverified, not impersonating, and banner not closed */
export const VerificationBannerContext = createContext<boolean>(false);

// Same condition as header: verification banner is visible when primary phone is unverified, not impersonating, and banner not closed
const isVerificationBannerVisible = (
    isPrimaryPhoneUnverified: boolean,
    isImpersonating: boolean,
    isVerificationBannerClosed: boolean,
) => isPrimaryPhoneUnverified && !isImpersonating && !isVerificationBannerClosed;

export const DashboardLayout: React.FC = () => {
    const sidebarRef = useRef<SidebarRef>(null);
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const userDetails = useSelector((state: RootState) => state.user.userDetails);
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const [assistantDrawerOpen, setAssistantDrawerOpen] = useState(false);
    const [openDrawerCount, setOpenDrawerCount] = useState(0);
    const [closedBannerPath, setClosedBannerPath] = useState<string | null>(null);
    const location = useLocation();
    const initialNotificationsFetch = useRef(false);
    const brandPrimaryColor = brandDetails?.PrimaryColour || colors.bluePreset[10];

    const isPrimaryPhoneUnverified = useMemo(() => {
        const primaryPhone = userDetails?.phoneNumbers?.find(phone => phone?.IsPrimary === true);
        return Boolean(primaryPhone && !primaryPhone.IsVerified);
    }, [userDetails?.phoneNumbers]);

    const isOnSettingsPage = useMemo(() => {
        const normalized = stripTrailingSlashes(location.pathname) || '/';
        return normalized === NavigationConfig.Settings || normalized.endsWith(NavigationConfig.Settings);
    }, [location.pathname]);

    const chatbotBottomOffset = useMemo(() => {
        const normalized = stripTrailingSlashes(location.pathname) || '/';
        const routesWithBottomActions = [
            NavigationConfig.DashboardMakePayment,
            NavigationConfig.ContractOverview,
            NavigationConfig.ContractDetails,
            NavigationConfig.Payments,
            NavigationConfig.Support,
        ];
        const hasBottomActions = routesWithBottomActions.some(
            route => normalized === route || normalized.endsWith(route),
        );
        return hasBottomActions ? 96 : 24;
    }, [location.pathname]);

    const isVerificationBannerClosed = closedBannerPath === location.pathname;

    const showVerificationBanner =
        isVerificationBannerVisible(
            isPrimaryPhoneUnverified,
            Boolean(impersonation?.isImpersonating),
            isVerificationBannerClosed,
        ) && !isOnSettingsPage;

    const handleMenuClick = () => {
        sidebarRef.current?.openDrawer();
    };

    // Determine if chatbot should be shown based on brand configuration (1 = enabled, 0 = disabled)
    // In local development, always show chatbot for testing
    const isChatbotEnabled = brandDetails?.IsChatBotEnabled === 1 || process.env.NODE_ENV === 'development';

    // Fetch notification count when dashboard layout mounts (user is authenticated)
    useEffect(() => {
        if (isLoggedIn) {
            if (initialNotificationsFetch.current) return;
            initialNotificationsFetch.current = true;

            const fetchUserNotificationsCount = async () => {
                try {
                    const response = await getUserNotificationsCount(false);
                    if (response.success) {
                        dispatch(setUserNotificationsCount(response?.data?.unreadCount));
                    }
                } catch (error) {
                    console.error('Error fetching notification count:', error);
                }
            };
            fetchUserNotificationsCount();
        }
    }, [isLoggedIn, dispatch]);

    const handlePortalHyperlinkClickCapture = (event: React.MouseEvent<HTMLElement>) => {
        if (!impersonation?.isImpersonating) return;
        const target = event.target as HTMLElement | null;
        const anchor = target?.closest('a');
        if (!anchor) return;
        event.preventDefault();
        event.stopPropagation();
    };

    const CHATBOT_TRIGGER_Z_INDEX = 1700;
    const isOtherDrawerOpen = openDrawerCount > 0;

    const drawerVisibilityValue = useMemo(
        () => ({
            setDrawerOpen: (open: boolean) => {
                setOpenDrawerCount(prev => {
                    const next = open ? prev + 1 : prev - 1;
                    return next < 0 ? 0 : next;
                });
            },
        }),
        [],
    );

    return (
        <div
            style={{ display: 'flex', height: '100vh', flexDirection: 'column' }}
            onClickCapture={handlePortalHyperlinkClickCapture}
            onAuxClickCapture={handlePortalHyperlinkClickCapture}
        >
            <Header
                onMenuClick={handleMenuClick}
                isVerificationBannerClosed={isVerificationBannerClosed}
                onCloseVerificationBanner={() => setClosedBannerPath(location.pathname)}
            />
            <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
                <Sidebar ref={sidebarRef} />
                <main
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        height: showVerificationBanner ? 'calc(100dvh - 103px)' : 'calc(100dvh - 66px)',
                        position: 'relative',
                        backgroundColor: colors.fullWhite,
                    }}
                >
                    <DrawerVisibilityContext.Provider value={drawerVisibilityValue}>
                        <VerificationBannerContext.Provider value={showVerificationBanner}>
                            <Outlet />
                        </VerificationBannerContext.Provider>
                    </DrawerVisibilityContext.Provider>
                    {isChatbotEnabled && (
                        <AuxCapAssistantDrawer
                            open={assistantDrawerOpen}
                            onClose={() => setAssistantDrawerOpen(false)}
                        />
                    )}
                </main>
            </div>

            {isChatbotEnabled && !assistantDrawerOpen && !isOtherDrawerOpen && !impersonation?.isImpersonating && (
                <Portal>
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: CHATBOT_TRIGGER_Z_INDEX,
                            pointerEvents: 'none',
                        }}
                    >
                        <Stack
                            position="absolute"
                            bottom={chatbotBottomOffset}
                            right={24}
                            sx={{
                                width: 44,
                                height: 44,
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                            }}
                            onClick={() => setAssistantDrawerOpen(true)}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setAssistantDrawerOpen(true);
                                }
                            }}
                            role="button"
                            aria-label="Open chat assistant"
                            tabIndex={0}
                        >
                            <Stack
                                sx={{
                                    position: 'relative',
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: colors.bluePreset[10],
                                    border: `1px solid ${colors.fullWhite}`,
                                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.24)',
                                }}
                            >
                                <ChatBG
                                    width={44}
                                    height={44}
                                    bubbleColor={brandPrimaryColor}
                                    style={{ display: 'block' }}
                                />
                                <Stack
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '50%',
                                        transform: 'translate(50%, -50%)',
                                    }}
                                >
                                    <span className="icon-chat" style={{ fontSize: '16px', color: colors.fullWhite }} />
                                </Stack>
                            </Stack>
                        </Stack>
                    </Box>
                </Portal>
            )}
        </div>
    );
};
